<script setup lang="ts">
import { computed } from 'vue';
import { $t } from '@/composables/translations/useTranslate.ts';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface Props {
  modelValue?: string | number | null;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  name?: string;
  id?: string;
  required?: boolean;
  label?: string;
  labelClass?: string;
  hasError?: boolean;
  errorMessage?: string;
  maxWidth?: string;
  wrapperClass?: string;
  selectClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholder: () => $t('ibe.common.select-option', 'Select an option'),
  disabled: false,
  loading: false,
  hasError: false,
  errorMessage: '',
  maxWidth: 'max-w-80',
  wrapperClass: '',
  selectClass: '',
  name: '',
  id: '',
  label: undefined,
  labelClass: undefined,
  required: false,
});

const isDisabled = computed(() => props.disabled || props.loading);

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null];
  change: [payload: { value: string | number; label: string }];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
}>();

const showError = computed(() => {
  return props.hasError && props.errorMessage;
});

const errorId = computed(() => {
  return props.errorMessage ? `${props.id || 'select'}-error` : undefined;
});

const selectId = computed(() => {
  return props.id || `select-${Math.random().toString(36).substring(2, 9)}`;
});

const wrapperClasses = computed(() => {
  const classes = ['relative', 'w-full', 'base-select', props.maxWidth];
  if (props.wrapperClass) {
    classes.push(props.wrapperClass);
  }
  return classes.join(' ');
});

const selectClasses = computed(() => {
  const baseClasses = [
    'appearance-none',
    'w-full',
    'px-3',
    'py-3',
    'pr-10', // Space for chevron icon
    'text-left',
    'transition-colors',
    'duration-200',
    'shadow-sm',
    'base-select-control',
  ];

  if (isDisabled.value) {
    if (!props.loading) {
      baseClasses.push('opacity-40');
    }
    baseClasses.push('cursor-not-allowed');
    if (props.hasError) {
      baseClasses.push('border-2', 'bg-highlight-2', 'border-error');
    } else {
      baseClasses.push('bg-white', 'border-b-2', 'border-b-text-primary');
    }
  } else {
    if (props.hasError) {
      baseClasses.push('border-2', 'bg-highlight-2', 'border-error', 'text-error');
    } else {
      baseClasses.push(
        'bg-white',
        'border-b-2',
        'border-b-text-primary',
        'hover:bg-actions-tertiary',
        'focus:bg-actions-tertiary',
        'focus:outline-none',
        'text-black'
      );
    }
  }

  if (props.selectClass) {
    baseClasses.push(props.selectClass);
  }

  return baseClasses.join(' ');
});

const chevronClasses = computed(() => {
  const baseClasses = [
    'absolute',
    'right-3',
    'top-1/2',
    'transform',
    '-translate-y-1/2',
    'w-5',
    'h-5',
    'pointer-events-none',
    'base-select-chevron',
  ];
  if (props.hasError) {
    baseClasses.push('text-error');
  } else {
    baseClasses.push('text-black');
  }
  return baseClasses.join(' ');
});

const computedLabelClass = computed(() => {
  const baseClass = 'block mb-1 text-sm font-medium text-grey';
  return props.labelClass ? `${baseClass} ${props.labelClass}` : baseClass;
});

const handleChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  const value =
    target.value === '' ? null : isNaN(Number(target.value)) ? target.value : Number(target.value);

  if (value === null) {
    emit('update:modelValue', null);
    return;
  }

  const option = props.options.find((opt) => String(opt.value) === String(value));
  if (option) {
    emit('update:modelValue', value);
    emit('change', { value: option.value, label: option.label });
  }
};

const handleBlur = (event: FocusEvent) => {
  emit('blur', event);
};

const handleFocus = (event: FocusEvent) => {
  emit('focus', event);
};
</script>

<template>
  <div class="control-wrapper">
    <label v-if="label" :for="selectId" :class="computedLabelClass">
      {{ label }}
    </label>
    <div :class="wrapperClasses">
      <select
        :id="selectId"
        :name="name"
        :disabled="isDisabled"
        :required="required"
        :class="selectClasses"
        :aria-describedby="errorId"
        :aria-invalid="hasError"
        :aria-required="required"
        :aria-busy="loading"
        :value="modelValue ?? ''"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
      >
        <option value="" :disabled="required">
          {{ placeholder }}
        </option>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>

      <!-- Loading spinner replaces chevron while fetching -->
      <span
        v-if="loading"
        class="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none"
        role="status"
        :aria-label="$t('ibe.common.loading', 'Loading')"
      >
        <span
          class="block w-5 h-5 border-2 border-current rounded-full border-t-transparent animate-spin"
        ></span>
      </span>

      <!-- Custom chevron icon -->
      <svg v-else :class="chevronClasses" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <!-- Error message -->
    <div v-if="showError" class="mt-2">
      <span
        :id="errorId"
        class="bg-error p-2.5 pr-12 inline-flex gap-2.5 items-center text-white text-sm"
        role="alert"
        aria-live="polite"
      >
        <i class="w-6 h-5 bg-white icon icon-warning shrink-0"></i>
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>

<style scoped>
select.base-select-control {
  background-image: none;
}

select.base-select-control::-ms-expand {
  display: none;
}

select.base-select-control:focus {
  outline: 2px solid #6c6b3c;
  outline-offset: -2px;
  clip-path: inset(-2px -2px 0 -2px);
}

@-moz-document url-prefix() {
  select.base-select-control {
    text-indent: 0;
    text-overflow: '';
  }
}
</style>
